import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Injectable, inject, type OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  StorageResponseAPI,
} from '@principle-theorem/ng-shared';
import {
  CustomReport,
  TimezoneResolver,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  ICustomReport,
  IReportRun,
  ReportBuilderDataSourceId,
  ReportingReference,
  type ICustomChartSettings,
  type ICustomReportChart,
} from '@principle-theorem/principle-core/interfaces';
import {
  BigQueryDataSource,
  IReportBuilderDisplay,
  IReportBuilderQuery,
  REPORT_BUILDER_DATA_SOURCES,
  ReportBuilderDataSource,
  ReportBuilderHelpers,
  ReportBuilderQuery,
  resolveDataSource,
  toCustomReport,
  toCustomReportQuery,
} from '@principle-theorem/reporting';
import { IAccessorContext } from '@principle-theorem/reporting/interfaces';
import {
  DataStreamEvent,
  Firestore,
  Timezone,
  WithRef,
  filterUndefined,
  firstValueFrom,
  snapshot,
  snapshotDefined,
  uid,
  type IDataStreamEvent,
} from '@principle-theorem/shared';
import { default as crossfilter } from 'crossfilter2';
import * as dc from 'dc';
import { isEqual } from 'lodash';
import { EMPTY, Observable, Subject, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  IReportBuildMoveToSectionFormData,
  IReportBuilderMoveToSectionDialogData,
  ReportBuilderMoveToSectionDialogComponent,
} from './report-builder-move-to-section-dialog/report-builder-move-to-section-dialog.component';
import {
  IReportBuilderColumnsDialogRequest,
  IReportBuilderColumnsDialogResponse,
  ReportBuilderColumnsDialogComponent,
} from './report-builder-results-table/report-builder-columns-dialog/report-builder-columns-dialog.component';
import { ReportEditorService } from './report-editor.service';

interface INdxEvent {
  ndx: crossfilter.Crossfilter<object>;
  eventType: crossfilter.EventType;
}

export interface IReportBuilderResults<T> {
  ndx: crossfilter.Crossfilter<T>;
  all: readonly T[];
  allCount: number;
  filtered: T[];
  filteredCount: number;
}

export interface IReportBuilderState<T = object> {
  isError: boolean;
  loading: boolean;
  progress?: number;
  dataSourceId?: ReportBuilderDataSourceId;
  display?: IReportBuilderDisplay;
  query?: IReportBuilderQuery;
  results?: IReportBuilderResults<T>;
  currentReport?: WithRef<ICustomReport>;
  loadedDatapoints: ReportingReference[];
  activeHistoricalRun?: WithRef<IReportRun>;
}

const initialState: IReportBuilderState = {
  isError: false,
  loading: false,
  loadedDatapoints: [],
};

export interface IResetChartFilter {
  chartUid?: string;
  dimensionId?: string;
}

@Injectable()
export class ReportBuilderStore
  extends ComponentStore<IReportBuilderState>
  implements OnDestroy
{
  private _organisation = inject(OrganisationService);
  private _reportEditor = inject(ReportEditorService);

  readonly resetFilter$ = new Subject<IResetChartFilter>();
  readonly resetCustomReportForm$ = new Subject<void>();
  readonly isLoading$ = this.select((state) => state.loading);
  readonly progress$ = this.select((state) => state.progress);
  readonly isError$ = this.select((state) => state.isError);
  readonly loadedDatapoints$ = this.select((state) => state.loadedDatapoints);
  readonly activeHistoricalRun$ = this.select(
    (state) => state.activeHistoricalRun
  );

  readonly query$ = this.select((state) => state.query);
  readonly dataSource$ = this.select((state) =>
    resolveDataSource(state.dataSourceId, REPORT_BUILDER_DATA_SOURCES)
  );

  readonly hasData$ = this.select((state) => !!state.results?.allCount);
  readonly hasNoData$ = this.select(
    (state) => !!state.results && !state.results.allCount
  );

  readonly results$ = this.select((state) => state.results);
  readonly ndx$ = this.select((state) => state.results?.ndx).pipe(
    distinctUntilChanged()
  );
  readonly crossfilterEvent$: Observable<INdxEvent>;

  readonly display$ = this.select((state) => state.display);
  readonly editMode$ = this.select((state) => state.query?.editMode ?? false);
  readonly currentReport$ = this.select((state) => state.currentReport).pipe(
    switchMap((customReport) =>
      customReport ? Firestore.doc$(customReport.ref) : of(undefined)
    )
  );
  readonly dynamicFilters$ = this.select(
    this.display$,
    (display) => display?.dynamicFilters ?? []
  );
  readonly dynamicFilterCount$ = this.select(
    this.dynamicFilters$,
    (dynamicFilters) => dynamicFilters.length
  );

  readonly hasEdits$ = this.select(
    this.display$,
    this.currentReport$,
    (display, report) => {
      if (!display || !report) {
        return false;
      }
      const columnsChanged = !isEqual(display?.columns, report?.columns);
      const chartsChanged = !isEqual(
        display?.chartSections,
        report?.chartSections
      );
      return columnsChanged || chartsChanged;
    }
  );

  readonly loadQuery = this.effect((query$: Observable<IReportBuilderQuery>) =>
    query$.pipe(
      tap((query) => {
        const stateChange: Partial<IReportBuilderState> = {
          query,
          isError: false,
          progress: undefined,
          loading: true,
          activeHistoricalRun: undefined,
        };
        if (query.clearCurrentResults) {
          stateChange.results = undefined;
          stateChange.loadedDatapoints = [];
        }
        this.patchState(stateChange);
      }),
      map((query) => ReportBuilderQuery.applySelectedDatesToFilters(query)),
      map((query) => ReportBuilderQuery.applyEditModeToColumns(query)),
      map((query) => ReportBuilderQuery.applyEditModeToCharts(query)),
      switchMap(async (query) => {
        const brandRef = query.scopeRequests.brand?.brandRef;
        const timezone = brandRef
          ? await TimezoneResolver.fromBrandRef(brandRef)
          : Timezone.AustraliaSydney;
        return { query, timezone };
      }),
      switchMap(({ query, timezone }) =>
        this._queryFactTable$(query, timezone).pipe(
          tap((event) => this._handleLoadStreamEvent(query, event)),
          catchError((error) => {
            this.patchState({
              isError: true,
              loading: false,
              progress: undefined,
            });
            // eslint-disable-next-line no-console
            console.error(error);
            return of(undefined);
          })
        )
      )
    )
  );

  readonly loadStorageResults = this.effect(
    (run$: Observable<WithRef<IReportRun>>) =>
      run$.pipe(
        tap((run) => {
          const currentReport = this.get().currentReport;
          const loadedDatapoints = currentReport
            ? ReportBuilderHelpers.getDatapoints(currentReport)
            : [];
          const currentDisplay = this.get().display;
          const display = this._getHistoricalDisplay(run, currentDisplay);
          this.patchState({
            isError: false,
            progress: undefined,
            loading: true,
            results: undefined,
            loadedDatapoints,
            activeHistoricalRun: run,
            display,
          });
        }),
        switchMap((run) => {
          if (!run.result?.storageUrl) {
            return EMPTY;
          }
          return this._api.load$<object[]>(run.result.storageUrl).pipe(
            tap((event) => this._handleStorageLoadEvent(event)),
            catchError((error) => {
              this.patchState({
                isError: true,
                loading: false,
                progress: undefined,
              });
              // eslint-disable-next-line no-console
              console.error(error);
              return of(undefined);
            })
          );
        })
      )
  );

  readonly ndxEvent = this.effect((event$: Observable<INdxEvent>) =>
    event$.pipe(tap((event) => this._handleNdxEvent(event)))
  );

  readonly setDisplay = this.updater(
    (state, display?: IReportBuilderDisplay) => ({
      ...state,
      display,
    })
  );
  readonly reset = this.updater(() => initialState);

  constructor(
    private _api: StorageResponseAPI,
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _router: Router
  ) {
    super(initialState);

    this.crossfilterEvent$ = this._listenToNdxEvents$();
    this.crossfilterEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.ndxEvent(event));
  }

  setDatasource(dataSourceId: ReportBuilderDataSourceId): void {
    const dataSource = resolveDataSource(
      dataSourceId,
      REPORT_BUILDER_DATA_SOURCES
    );
    if (!dataSource) {
      this.patchState({ isError: true });
      return;
    }
    this.patchState({
      dataSourceId,
      display: {
        dynamicFilters: [],
        columns: ReportBuilderDataSource.getDefaultColumns(dataSource),
        chartSections: [],
      },
    });
  }

  setCurrentReport(customReport: WithRef<ICustomReport>): void {
    this.patchState({
      currentReport: customReport,
      dataSourceId: customReport.dataSource,
      display: {
        chartSections: customReport.chartSections,
        columns: customReport.columns,
        dynamicFilters: [],
      },
    });
  }

  async resetActiveFilters(): Promise<void> {
    dc.filterAll();
    dc.renderAll();

    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }
    this.setDisplay({ ...current, dynamicFilters: [] });
  }

  async reloadReport(
    queryUpdates?: Partial<IReportBuilderQuery>
  ): Promise<void> {
    const currentQuery = await firstValueFrom(this.query$);
    const report = await firstValueFrom(this.currentReport$);
    if (!report || !currentQuery) {
      return;
    }

    const query = toCustomReportQuery(
      report,
      queryUpdates?.scopeRequests ?? currentQuery.scopeRequests,
      queryUpdates?.staticFilters ?? currentQuery.staticFilters
    );
    if (!query) {
      return;
    }
    if (queryUpdates?.clearCurrentResults) {
      query.clearCurrentResults = true;
    }
    query.filterDate = queryUpdates?.filterDate ?? currentQuery.filterDate;
    query.filterDateRange =
      queryUpdates?.filterDateRange ?? currentQuery.filterDateRange;

    this.loadQuery(query);
  }

  async editColumns(): Promise<void> {
    if (this.get().activeHistoricalRun) {
      return;
    }
    const dataSource = await firstValueFrom(this.dataSource$);
    const display = await firstValueFrom(this.display$);
    if (!dataSource || !display) {
      return;
    }
    const data = {
      dataSource,
      columns: display?.columns ?? [],
    };

    const response = await this._dialog
      .open<
        ReportBuilderColumnsDialogComponent,
        IReportBuilderColumnsDialogRequest,
        IReportBuilderColumnsDialogResponse
      >(
        ReportBuilderColumnsDialogComponent,
        DialogPresets.large({ maxHeight: '80vh', data })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }
    this.setDisplay({ ...display, columns: response.columns });
  }

  async addChart<T extends ICustomChartSettings>(
    chart: ICustomReportChart<T>,
    sectionName: string = 'Charts',
    index?: number
  ): Promise<void> {
    const newChart = {
      ...chart,
      uid: uid(),
    };

    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }

    const existingSection = current.chartSections.find(
      (section) => section.name === sectionName
    );

    if (!existingSection) {
      const newSection = CustomReport.chartSection(sectionName, [newChart]);
      this.setDisplay({
        ...current,
        chartSections: [...current.chartSections, newSection],
      });
      return;
    }

    const chartSections = current.chartSections.map((section) => {
      if (section.uid !== existingSection.uid) {
        return section;
      }

      const charts = [...section.charts];
      charts.splice(index ?? charts.length, 0, newChart);

      return {
        ...section,
        charts,
      };
    });

    this.setDisplay({ ...current, chartSections });
  }

  async editChart(chart: ICustomReportChart): Promise<void> {
    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }

    const dataSource = await snapshot(this.dataSource$);
    if (!dataSource) {
      return;
    }
    const filteredData = await snapshot(
      this.results$.pipe(map((results) => results?.filtered ?? []))
    );
    const ndx = await firstValueFrom(this.ndx$);

    const response = await this._reportEditor.editChart(
      dataSource,
      chart,
      filteredData,
      ndx
    );
    if (!response) {
      return;
    }
    await this.patchChart(response);
  }

  async editTable(chart: ICustomReportChart): Promise<void> {
    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }
    const response = await this._reportEditor.editTable(chart);
    if (!response) {
      return;
    }
    await this.patchChart(response);
  }

  async patchChart(chart: ICustomReportChart): Promise<void> {
    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }
    const chartSections = this._reportEditor.upsertChart(
      current.chartSections,
      chart
    );
    this.setDisplay({ ...current, chartSections });
  }

  async moveToSection(
    chart: ICustomReportChart,
    currentSectionName: string
  ): Promise<void> {
    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }

    const data: IReportBuilderMoveToSectionDialogData = {
      sectionNames: current.chartSections
        .map((section) => section.name)
        .filter((name) => name !== currentSectionName)
        .sort(),
    };

    const newSection = await this._dialog
      .open<
        ReportBuilderMoveToSectionDialogComponent,
        IReportBuilderMoveToSectionDialogData,
        IReportBuildMoveToSectionFormData
      >(
        ReportBuilderMoveToSectionDialogComponent,
        DialogPresets.small({ data })
      )
      .afterClosed()
      .toPromise();

    if (!newSection) {
      return;
    }

    const chartSections = current.chartSections.map((section) => {
      if (section.name === currentSectionName) {
        const charts = [...section.charts];
        const chartIndex: number = charts.findIndex(
          (existingChart) => existingChart.uid === chart.uid
        );
        charts.splice(chartIndex, 1);
        return {
          ...section,
          charts,
        };
      }

      if (section.name === newSection?.sectionName) {
        return {
          ...section,
          charts: [...section.charts, chart],
        };
      }

      return section;
    });

    this.setDisplay({ ...current, chartSections });
  }

  async duplicateChart(
    chart: ICustomReportChart,
    sectionName: string
  ): Promise<void> {
    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }

    const chartSections = current.chartSections.map((section) => {
      if (section.name !== sectionName) {
        return section;
      }

      const charts = [...section.charts];
      const chartIndex: number = charts.findIndex(
        (existingChart) => existingChart.uid === chart.uid
      );
      charts.splice(chartIndex, 0, { ...chart, uid: uid() });

      return {
        ...section,
        charts,
      };
    });
    this.setDisplay({ ...current, chartSections });
  }

  async moveChartLeft(
    chart: ICustomReportChart,
    sectionName: string
  ): Promise<void> {
    await this.moveChart(-1, chart, sectionName);
  }

  async moveChartRight(
    chart: ICustomReportChart,
    sectionName: string
  ): Promise<void> {
    await this.moveChart(1, chart, sectionName);
  }

  async moveChart(
    indexOffset: number,
    chart: ICustomReportChart,
    sectionName: string
  ): Promise<void> {
    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }

    const chartSections = current.chartSections.map((section) => {
      if (section.name !== sectionName) {
        return section;
      }
      const sectionCharts = [...section.charts];
      const currentIndex = sectionCharts.findIndex(
        (item) => item.uid === chart.uid
      );
      if (currentIndex === -1) {
        return section;
      }
      moveItemInArray(sectionCharts, currentIndex, currentIndex + indexOffset);
      return { ...section, charts: sectionCharts };
    });
    this.setDisplay({ ...current, chartSections });
  }

  async deleteChart(chart: ICustomReportChart): Promise<void> {
    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }

    const chartSections = current.chartSections.map((section) => ({
      ...section,
      charts: section.charts.filter((item) => item.uid !== chart.uid),
    }));

    const dynamicFilters = current.dynamicFilters.filter(
      (filter) => filter.id !== chart.uid
    );
    this.setDisplay({ ...current, chartSections, dynamicFilters });
    this.resetFilter$.next({ chartUid: chart.uid });
  }

  async deleteFilter(filterId: string): Promise<void> {
    const current = await snapshot(this.display$);
    if (!current) {
      return;
    }
    const dynamicFilters = current.dynamicFilters.filter(
      (filter) => filter.id !== filterId
    );
    this.setDisplay({ ...current, dynamicFilters });
  }

  async saveAsCustomReport(): Promise<void> {
    const state = await snapshot(this.state$);
    if (!state.query || !state.display) {
      return;
    }
    const response = await this._reportEditor.openCustomReportDialog();
    if (!response || !response.formData) {
      return;
    }
    const staffer = await snapshotDefined(this._organisation.staffer$);
    const creator = stafferToNamedDoc(staffer);
    const customReport = toCustomReport(
      { ...response.formData, creator },
      state.query,
      state.display
    );
    const brandRef = state.query.scopeRequests.brand?.brandRef;
    if (!brandRef) {
      throw new Error('Brand is required');
    }

    const customReportRef = await Firestore.addDoc(
      CustomReport.col({ ref: brandRef }),
      customReport
    );

    this._snackbar
      .open('Custom report added', 'Open Custom Report', {
        duration: 5000,
      })
      .onAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () =>
          void this._router.navigate([
            'reporting',
            'custom-reports',
            customReportRef.id,
          ])
      );
  }

  async saveReportChanges(): Promise<void> {
    const display = await snapshot(this.display$);
    const report = await snapshot(this.currentReport$);
    const hasEdits = await snapshot(this.hasEdits$);
    if (!hasEdits || !report) {
      return;
    }

    const updates = {
      columns: display?.columns ?? report.columns,
      chartSections: display?.chartSections ?? report.chartSections,
    };
    await Firestore.patchDoc(report.ref, {
      ...updates,
    });
    this.patchState({
      currentReport: {
        ...report,
        ...updates,
      },
    });

    this.patchState((state) => {
      if (!state.query) {
        return state;
      }

      return {
        query: {
          ...state.query,
          editMode: false,
        },
      };
    });
  }

  async discardReportChanges(): Promise<void> {
    const report = await snapshot(this.currentReport$);
    if (!report) {
      return;
    }
    this.setDisplay({
      columns: report.columns,
      chartSections: report.chartSections,
      dynamicFilters: [],
    });
    this.resetCustomReportForm$.next();
  }

  private _handleLoadStreamEvent<T extends object>(
    query: IReportBuilderQuery,
    event: IDataStreamEvent<T[]>
  ): void {
    if (DataStreamEvent.isProgress(event)) {
      this.patchState({
        loading: true,
        progress: event.value * 100,
      });
    }

    if (DataStreamEvent.isComplete(event)) {
      const ndx = crossfilter<T>(event.value);
      const results = this._getResults(ndx);
      const loadedDatapoints = ReportBuilderHelpers.getDatapoints(query);
      this.patchState({ results, loadedDatapoints, loading: false });
    }
  }

  private _handleStorageLoadEvent<T extends object>(
    event: IDataStreamEvent<T[]>
  ): void {
    if (DataStreamEvent.isProgress(event)) {
      this.patchState({
        loading: true,
        progress: event.value * 100,
      });
    }

    if (DataStreamEvent.isComplete(event)) {
      const ndx = crossfilter<T>(event.value);
      const results = this._getResults(ndx);
      this.patchState({ results, loading: false });
    }
  }

  private _queryFactTable$<T extends object>(
    query: IReportBuilderQuery,
    timezone: Timezone
  ): Observable<IDataStreamEvent<T[]>> {
    const orgRef$ = this._organisation.organisation$.pipe(
      filterUndefined(),
      map((org) => org.ref)
    );
    const bigQuery = new BigQueryDataSource<T>(this._api, orgRef$);
    const dataSource = resolveDataSource(
      query.dataSource,
      REPORT_BUILDER_DATA_SOURCES
    );
    if (!dataSource) {
      throw new Error('DataSource not found');
    }

    const columns = dataSource.getQueryColumns(query.columns);
    const charts = dataSource.getQueryCharts(
      query.chartSections?.flatMap((section) => section.charts) ?? []
    );

    const context: IAccessorContext = { timezone };
    const reportingQuery = dataSource.buildQuery(
      columns,
      charts,
      query.staticFilters ?? [],
      context
    );

    return bigQuery.get$(reportingQuery, query.scopeRequests, {
      customReportRef: query.customReportRef,
      filterDate: query.filterDate,
      filterDateRange: query.filterDateRange,
    });
  }

  private _handleNdxEvent(event: INdxEvent): void {
    const results = this._getResults(event.ndx);
    this.patchState({ results });
  }

  private _getResults<T extends object>(
    ndx: crossfilter.Crossfilter<T>
  ): IReportBuilderResults<T> {
    const all = ndx.all();
    const filtered = ndx.allFiltered();
    return {
      ndx,
      all,
      allCount: all.length,
      filtered,
      filteredCount: filtered.length,
    };
  }

  private _listenToNdxEvents$(): Observable<INdxEvent> {
    return this.ndx$.pipe(
      filterUndefined(),
      switchMap(
        (ndx) =>
          new Observable<INdxEvent>((observer) =>
            ndx.onChange((eventType) => observer.next({ ndx, eventType }))
          )
      )
    );
  }

  private _getHistoricalDisplay(
    run: WithRef<IReportRun>,
    currentDisplay?: IReportBuilderDisplay
  ): IReportBuilderDisplay | undefined {
    const reportSnapshot = run.reportSnapshot;
    if (!reportSnapshot?.columns && !reportSnapshot?.chartSections) {
      return currentDisplay;
    }
    return {
      columns: reportSnapshot.columns ?? currentDisplay?.columns ?? [],
      chartSections:
        reportSnapshot.chartSections ?? currentDisplay?.chartSections ?? [],
      dynamicFilters: currentDisplay?.dynamicFilters ?? [],
    };
  }
}
